import { z } from "zod";
import { getBrowserTimeZone } from "../../../../lib/date-time";

export const predefinedPoints = [100, 200, 300, 400, 500, 750, 1000, 1250, 1500] as const;

export type EventEditorFormValues = {
  image: File | null;
  imageUrl: string;
  name: string;
  description: string;
  location: string;
  lat: string;
  lon: string;
  radius: string;
  points: string;
  eventDate: Date | null;
  start: string;
  end: string;
  checkInTime: string;
  guestAttendanceVerification: string;
  timezone: string;
  neighborhoodId: string;
  categoryId: string;
  subCategoryIds: string[];
};

export type EventEditorSubmitValues = EventEditorFormValues;

export const createDefaultEventEditorValues = (): EventEditorFormValues => ({
  image: null,
  imageUrl: "",
  name: "",
  description: "",
  location: "",
  lat: "",
  lon: "",
  radius: "",
  points: "",
  eventDate: new Date(),
  start: "",
  end: "",
  checkInTime: "",
  guestAttendanceVerification: "",
  timezone: getBrowserTimeZone(),
  neighborhoodId: "",
  categoryId: "",
  subCategoryIds: [],
});

export const defaultEventEditorValues: EventEditorFormValues = createDefaultEventEditorValues();

const requiredDateSchema = (message: string) =>
  z
    .date()
    .nullable()
    .refine((value): value is Date => value instanceof Date, { message });

const eventEditorSchemaBase = z.object({
  image: z.custom<File | null>((value) => value === null || (typeof File !== "undefined" && value instanceof File), {
    message: "Please upload event image",
  }),
  imageUrl: z.string(),
  name: z.string().trim().min(1, "Please enter event name"),
  description: z.string().trim().min(1, "Please enter description"),
  location: z.string().trim().min(1, "Please enter location"),
  lat: z.string().trim().min(1, "Please select location on map"),
  lon: z.string().trim().min(1, "Please select location on map"),
  radius: z.string().trim().min(1, "Please select check-in radius"),
  points: z
    .string()
    .trim()
    .min(1, "Please enter points")
    .refine((value) => Number(value) > 0, "Please enter points"),
  eventDate: requiredDateSchema("Please select event date"),
  start: z.string().trim().min(1, "Please select start time"),
  end: z.string().trim().min(1, "Please select end time"),
  checkInTime: z.string().optional().default(""),
  guestAttendanceVerification: z.string().optional().default(""),
  timezone: z.string().trim().min(1, "Please select timezone"),
  neighborhoodId: z.string().trim().min(1, "Please select neighborhood"),
  categoryId: z.string().trim().min(1, "Please select category"),
  subCategoryIds: z.array(z.string()).default([]),
}) as z.ZodType<EventEditorFormValues>;

export const createEventEditorSchema = eventEditorSchemaBase.superRefine((data, ctx) => {
  if (!data.image) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["image"],
      message: "Please upload event image",
    });
  }
});

export const updateEventEditorSchema = eventEditorSchemaBase;
